import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { glorifyApiClient } from '../../../services/api/glorify'
import type {
  MarketSearchResult,
  MarketQuoteResponse,
  MarketsHistoryResponse,
  PriceHistoryPoint,
} from '../../../services/api/glorify'
import { useSymbolSearch } from '../../../hooks/useSymbolSearch'
import {
  formatCurrency,
  formatSignedCurrency,
  formatSignedPercent,
  formatCompactCurrency,
  changeClass,
  formatDateShort,
} from './marketsFormat'
import { md, CHART_GRID, TOOLTIP_STYLE, type SectionError, toSectionError } from './marketsTheme'
import { SectionErrorBanner, StaleChip } from './marketsShared'

type RangeOption = '1M' | '3M' | '6M' | '1Y'

const RANGE_DAYS: Record<RangeOption, number> = { '1M': 30, '3M': 90, '6M': 180, '1Y': 365 }

export default function StockQuotePage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const symbolSearch = useSymbolSearch(searchQuery)
  const searchBoxRef = useRef<HTMLDivElement>(null)

  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null)
  const [quote, setQuote] = useState<MarketQuoteResponse | null>(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [quoteError, setQuoteError] = useState<SectionError | null>(null)
  const [history, setHistory] = useState<MarketsHistoryResponse | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<SectionError | null>(null)
  const [range, setRange] = useState<RangeOption>('1Y')

  const loadSymbol = useCallback(async (symbol: string): Promise<void> => {
    setSelectedSymbol(symbol)
    setShowDropdown(false)
    setSearchQuery('')

    setQuoteLoading(true)
    setQuoteError(null)
    try {
      const q = await glorifyApiClient.marketsQuote(symbol)
      setQuote(q)
    } catch (err) {
      setQuote(null)
      setQuoteError(toSectionError(err, 'Failed to load quote'))
    } finally {
      setQuoteLoading(false)
    }

    setHistoryLoading(true)
    setHistoryError(null)
    try {
      const h = await glorifyApiClient.marketsHistory(symbol)
      setHistory(h)
    } catch (err) {
      setHistory(null)
      setHistoryError(toSectionError(err, 'Failed to load price history'))
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  // Deep-link support: /dashboard/markets/stock-quote?symbol=AAPL (used by
  // MoversPage's "view quote" row action) pre-loads that symbol on mount.
  useEffect(() => {
    const symbolParam = searchParams.get('symbol')
    if (symbolParam) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void loadSymbol(symbolParam)
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('symbol')
        return next
      }, { replace: true })
    }
    // Intentionally run only on mount — loadSymbol/setSearchParams are stable
    // enough for this one-shot deep-link read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Close search dropdown on outside click ────────────────────────────
  useEffect(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectSearchResult = (result: MarketSearchResult): void => {
    void loadSymbol(result.symbol)
  }

  // ── Client-side range filter over the already-fetched 1y series ───────
  // FMP returns historical series newest-first; sort ascending so time flows
  // left → right on the chart (latest date on the right).
  const filteredHistoryPoints = useMemo(() => {
    if (!history) return []
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - RANGE_DAYS[range])
    return history.points
      .filter((p) => new Date(p.date) >= cutoff)
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [history, range])

  return (
    <>
      <div className="gd-page-header">
        <div className="gd-page-header__text">
          <h1>Stock Quote</h1>
          <p>Search any symbol for a live quote and price history, powered by Financial Modeling Prep.</p>
        </div>
      </div>

      <div className="gd-section">
        <div className="gd-search" ref={searchBoxRef}>
          <div className="gd-search__input-wrap">
            <span className="material-icons" aria-hidden="true">search</span>
            <input
              className="gd-search__input"
              type="text"
              placeholder="Search by symbol or name…"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setShowDropdown(true)
              }}
              onFocus={() => setShowDropdown(true)}
              aria-label="Search for a stock symbol"
            />
          </div>
          {showDropdown && searchQuery.trim().length > 0 && (
            <div className="gd-search__dropdown">
              {symbolSearch.isLoading ? (
                <div className="gd-search__empty">Searching…</div>
              ) : symbolSearch.error ? (
                <div className="gd-search__empty">{symbolSearch.error}</div>
              ) : symbolSearch.results.length === 0 ? (
                <div className="gd-search__empty">No matches.</div>
              ) : (
                symbolSearch.results.map((r) => (
                  <button
                    key={`${r.symbol}-${r.exchange}`}
                    className="gd-search__item"
                    onClick={() => handleSelectSearchResult(r)}
                  >
                    <span><b>{r.symbol}</b> — {r.name}</span>
                    <small>{r.exchange_full_name || r.exchange}</small>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {selectedSymbol && (
          <div style={{ marginTop: 20 }}>
            <SectionErrorBanner error={quoteError} />
            {quoteLoading ? (
              <div className="gd-empty">
                <div className="gd-spinner" aria-label="Loading quote" />
              </div>
            ) : quote ? (
              <div className="gd-card gd-card--elevated">
                <div className="gd-quote-card__header">
                  <span className="gd-quote-card__symbol">{quote.symbol}</span>
                  <span className="gd-quote-card__name">{quote.name}</span>
                  {quote.stale && <StaleChip />}
                </div>
                <div className="gd-quote-card__price" style={{ marginBottom: 4 }}>
                  {formatCurrency(quote.price)}
                </div>
                <div className={changeClass(quote.change)} style={{ marginBottom: 18 }}>
                  {formatSignedCurrency(quote.change)} ({formatSignedPercent(quote.change_percentage)})
                </div>

                <div className="gd-quote-grid">
                  <div>
                    <div className="gd-quote-stat__label">Day range</div>
                    <div className="gd-quote-stat__value">
                      {formatCurrency(quote.day_low)} – {formatCurrency(quote.day_high)}
                    </div>
                  </div>
                  <div>
                    <div className="gd-quote-stat__label">52-week range</div>
                    <div className="gd-quote-stat__value">
                      {formatCurrency(quote.year_low)} – {formatCurrency(quote.year_high)}
                    </div>
                  </div>
                  <div>
                    <div className="gd-quote-stat__label">Market cap</div>
                    <div className="gd-quote-stat__value">{formatCompactCurrency(quote.market_cap)}</div>
                  </div>
                  <div>
                    <div className="gd-quote-stat__label">Open</div>
                    <div className="gd-quote-stat__value">{formatCurrency(quote.open)}</div>
                  </div>
                  <div>
                    <div className="gd-quote-stat__label">Previous close</div>
                    <div className="gd-quote-stat__value">{formatCurrency(quote.previous_close)}</div>
                  </div>
                  <div>
                    <div className="gd-quote-stat__label">50-day avg</div>
                    <div className="gd-quote-stat__value">{formatCurrency(quote.price_avg_50)}</div>
                  </div>
                  <div>
                    <div className="gd-quote-stat__label">200-day avg</div>
                    <div className="gd-quote-stat__value">{formatCurrency(quote.price_avg_200)}</div>
                  </div>
                  <div>
                    <div className="gd-quote-stat__label">Exchange</div>
                    <div className="gd-quote-stat__value">{quote.exchange}</div>
                  </div>
                </div>

                <div className="gd-segmented" role="group" aria-label="Chart range" style={{ marginBottom: 12 }}>
                  {(['1M', '3M', '6M', '1Y'] as const).map((r) => (
                    <button
                      key={r}
                      className={`gd-segmented__btn${range === r ? ' gd-segmented__btn--active' : ''}`}
                      onClick={() => setRange(r)}
                      aria-pressed={range === r}
                    >
                      {r}
                    </button>
                  ))}
                </div>

                <SectionErrorBanner error={historyError} />
                {historyLoading ? (
                  <div className="gd-empty">
                    <div className="gd-spinner" aria-label="Loading price history" />
                  </div>
                ) : filteredHistoryPoints.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={filteredHistoryPoints} margin={{ left: 0, right: 16 }}>
                      <defs>
                        <linearGradient id="gd-price-gradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={md.primary} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={md.primary} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={CHART_GRID} vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d: string) => formatDateShort(d)}
                        stroke={md.onSurfaceVariant}
                        fontSize={11}
                        minTickGap={32}
                      />
                      <YAxis
                        domain={['auto', 'auto']}
                        tickFormatter={(v: number) => formatCurrency(v, 0)}
                        stroke={md.onSurfaceVariant}
                        fontSize={11}
                        width={56}
                      />
                      <Tooltip
                        {...TOOLTIP_STYLE}
                        labelFormatter={(d) => formatDateShort(String(d))}
                        formatter={(value, _name, item) => {
                          const p = item?.payload as PriceHistoryPoint | undefined
                          const closeLabel = formatCurrency(Number(value))
                          if (!p) return [closeLabel, 'Close']
                          return [
                            `${closeLabel} (${formatSignedPercent(p.change_percent)}) · VWAP ${formatCurrency(p.vwap)}`,
                            'Close',
                          ]
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="close"
                        stroke={md.primary}
                        strokeWidth={2}
                        fill="url(#gd-price-gradient)"
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : !historyError ? (
                  <div className="gd-empty">
                    <p>No price history available for this range.</p>
                  </div>
                ) : null}
              </div>
            ) : !quoteError ? (
              <div className="gd-empty">
                <span className="material-icons" aria-hidden="true">search_off</span>
                <p>No quote data.</p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  )
}
